/**
 * APICore.ai 图生图API服务
 * 基于 gemini-2.5-flash-image 模型
 */

import { 
  ImageGenerationRequest, 
  ImageGenerationResponse, 
  ImageGenerationConfig 
} from '@/types/imageGeneration'

class ImageGenerationService {
  private config: ImageGenerationConfig
// #apicore
// VITE_APICORE_API_KEY = sk-ylGE4sZTaCWo9oS1HznTQUVcmht9C6uVK3zPMqGsXG2JnHjX
// VITE_APICORE_BASE_URL = https://api.apicore.ai
  constructor() {
    this.config = {
      apiKey: import.meta.env.VITE_APICORE_API_KEY,
      baseUrl: import.meta.env.VITE_APICORE_BASE_URL,
      defaultPrompt: 'high quality, detailed, professional, artistic',
      defaultNegativePrompt: 'low quality, blurry, distorted, ugly, bad anatomy',
      defaultWidth: 1024,
      defaultHeight: 1024,
      defaultSteps: 20,
      defaultGuidanceScale: 7.5
    }
  }

  /**
   * 验证API配置
   */
  private validateConfig(): boolean {
    if (!this.config.apiKey) {
      console.error('APICore API key is not configured')
      return false
    }
    return true
  }

  /**
   * 将图片文件转换为base64
   */
  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        // 移除data:image/...;base64,前缀，只保留base64数据
        const base64 = result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  /**
   * 生成图片 - 使用 gemini-2.5-flash-image 模型
   */
  async generateImage(request: ImageGenerationRequest): Promise<ImageGenerationResponse> {
    if (!this.validateConfig()) {
      return {
        success: false,
        error: 'API配置错误：请检查APICore API密钥'
      }
    }

    try {
      const startTime = Date.now()
      
      // 将图片文件转换为base64
      const imageBase64 = await this.fileToBase64(request.image)
      
      // 构建请求体 - 使用 gemini-2.5-flash-image 模型
      const requestBody = {
        model: "gemini-2.5-flash-image",
        messages: [
          {
            role: "user",
            content: [
              {
                type: "text",
                text: request.prompt
              },
              {
                type: "image_url",
                image_url: {
                  url: `data:image/jpeg;base64,${imageBase64}`
                }
              }
            ]
          }
        ],
        max_tokens: 1000,
        temperature: 0.7
      }

      // 发送API请求到 chat/completions 端点
      const response = await fetch(`${this.config.baseUrl}/v1/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.config.apiKey}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })

      const processingTime = Date.now() - startTime

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return {
          success: false,
          error: errorData.error?.message || `API请求失败: ${response.status} ${response.statusText}`,
          processingTime
        }
      }

      const data = await response.json()

      // 解析响应数据
      if (data.choices && data.choices.length > 0) {
        const choice = data.choices[0]
        if (choice.message && choice.message.content) {
          // 对于图生图，响应可能包含生成的图片URL或描述
          // 这里需要根据实际API响应格式调整
          const content = choice.message.content
          
          // 如果响应包含图片URL，提取它
          const imageUrlMatch = content.match(/https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp)/i)
          const imageUrl = imageUrlMatch ? imageUrlMatch[0] : null
          
          return {
            success: true,
            imageUrl: imageUrl,
            requestId: data.id,
            processingTime,
            // 如果API返回的是文本描述而不是图片URL，我们可以在这里处理
            textResponse: imageUrl ? null : content
          }
        }
      }

      return {
        success: false,
        error: 'API响应格式不正确',
        processingTime
      }

    } catch (error) {
      console.error('Image generation error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : '未知错误'
      }
    }
  }

  /**
   * 获取默认配置
   */
  getDefaultConfig() {
    return {
      prompt: this.config.defaultPrompt,
      negativePrompt: this.config.defaultNegativePrompt,
      width: this.config.defaultWidth,
      height: this.config.defaultHeight,
      steps: this.config.defaultSteps,
      guidanceScale: this.config.defaultGuidanceScale
    }
  }

  /**
   * 验证图片文件
   */
  validateImageFile(file: File): { valid: boolean; error?: string } {
    // 检查文件类型
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: '不支持的图片格式，请上传 JPEG、PNG 或 WebP 格式的图片'
      }
    }

    // 检查文件大小 (最大10MB)
    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      return {
        valid: false,
        error: '图片文件过大，请上传小于10MB的图片'
      }
    }

    return { valid: true }
  }

  /**
   * 创建图片预览URL
   */
  createImagePreview(file: File): string {
    return URL.createObjectURL(file)
  }

  /**
   * 清理预览URL
   */
  revokeImagePreview(url: string): void {
    URL.revokeObjectURL(url)
  }
}

// 导出单例实例
export const imageGenerationService = new ImageGenerationService()
export default imageGenerationService
